import { useState, type DragEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, GripVertical, Pencil, Trash2 } from 'lucide-react';
import type { MeasurementTemplate, ResolvedMeasurementField } from '@tailoring-crm/shared';
import { cn, Badge, Button } from '@tailoring-crm/ui';

/**
 * The field list, in the order a tailor will meet it on the capture form.
 *
 * Reordering is offered two ways, and that is deliberate rather than indecisive. Buttons are
 * the primary mechanism: the shop owner doing this is on a 360px phone, where dragging a row
 * fights with scrolling the page and loses — the same conclusion the media uploader reached,
 * for the same reason. Dragging is added on top for a pointer, where it is genuinely the
 * faster way to move a field five rows up. Both produce the same request, and the buttons are
 * what a keyboard and a screen reader use.
 *
 * Fields are shown grouped under their headings, because that is how the capture form will
 * read and the editor should not require imagination.
 */

export interface TemplateFieldListProps {
  template: MeasurementTemplate;
  onEdit: (field: ResolvedMeasurementField) => void;
  onDelete: (field: ResolvedMeasurementField) => void;
  /** The complete new order of field keys. */
  onReorder: (order: string[]) => void;
  disabled?: boolean;
}

/** Move one key to a new index, returning the whole order the API expects. */
function moved(keys: readonly string[], from: number, to: number): string[] {
  const next = [...keys];
  const [key] = next.splice(from, 1);
  if (key === undefined) return next;
  next.splice(to, 0, key);
  return next;
}

export function TemplateFieldList({
  template,
  onEdit,
  onDelete,
  onReorder,
  disabled = false,
}: TemplateFieldListProps): ReactElement {
  const { t } = useTranslation();
  const [dragKey, setDragKey] = useState<string | null>(null);

  const fields = template.fields;
  const keys = fields.map((field) => field.key);

  const move = (from: number, to: number): void => {
    if (to < 0 || to >= fields.length || from === to) return;
    onReorder(moved(keys, from, to));
  };

  const onDrop = (event: DragEvent<HTMLLIElement>, toIndex: number): void => {
    event.preventDefault();
    if (!dragKey) return;
    const from = keys.indexOf(dragKey);
    setDragKey(null);
    if (from >= 0) move(from, toIndex);
  };

  // Consecutive runs under one heading, so the list reads the way the form will.
  const sections: { group: string | null; fields: ResolvedMeasurementField[] }[] = [];
  for (const field of fields) {
    const last = sections.at(-1);
    if (last && last.group === (field.group ?? null)) last.fields.push(field);
    else sections.push({ group: field.group ?? null, fields: [field] });
  }

  return (
    <div className="flex flex-col gap-4">
      {sections.map((section, sectionIndex) => (
        <section
          key={`${section.group ?? 'ungrouped'}-${sectionIndex}`}
          className="flex flex-col gap-2"
        >
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {section.group ?? t('measurements.ungrouped')}
          </h4>
          <ul className="flex flex-col gap-2">
            {section.fields.map((field) => {
              const index = keys.indexOf(field.key);
              return (
                <li
                  key={field.key}
                  draggable={!disabled}
                  onDragStart={() => setDragKey(field.key)}
                  onDragEnd={() => setDragKey(null)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => onDrop(event, index)}
                  className={cn(
                    'flex flex-col gap-2 rounded-lg border border-border p-3 sm:flex-row sm:items-center sm:gap-3',
                    !field.active && 'opacity-60',
                    dragKey === field.key && 'border-primary',
                  )}
                >
                  <GripVertical
                    aria-hidden="true"
                    className="hidden size-4 shrink-0 cursor-grab text-muted-foreground sm:block"
                  />

                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      {field.label}
                      <span className="text-xs font-normal text-muted-foreground">
                        {t(`measurements.unit.${field.resolvedUnit}Short`)}
                      </span>
                      {field.required ? (
                        <Badge variant="secondary">{t('measurements.requiredBadge')}</Badge>
                      ) : null}
                      {!field.active ? (
                        <Badge variant="warning">{t('measurements.inactiveBadge')}</Badge>
                      ) : null}
                    </p>
                    {field.helperText ? (
                      <p className="text-sm text-muted-foreground">{field.helperText}</p>
                    ) : null}
                    {/* The key, always visible. A shop that can see it stops being surprised
                        that renaming the label leaves it alone. */}
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t('measurements.keyNote', { key: field.key })}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={disabled || index === 0}
                      aria-label={t('measurements.moveUp', { label: field.label })}
                      onClick={() => move(index, index - 1)}
                    >
                      <ChevronUp aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={disabled || index === fields.length - 1}
                      aria-label={t('measurements.moveDown', { label: field.label })}
                      onClick={() => move(index, index + 1)}
                    >
                      <ChevronDown aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={disabled}
                      aria-label={t('measurements.editField', { label: field.label })}
                      onClick={() => onEdit(field)}
                    >
                      <Pencil aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={disabled}
                      aria-label={t('measurements.deleteFieldTitle', { label: field.label })}
                      onClick={() => onDelete(field)}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
